"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Maximize2, Snowflake, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useNow } from "../lib/use-now";
import { formatContestMinute, formatRemaining, remainingUntil } from "../lib/format";
import type { PublicContestDetail } from "../types";
import { ContestStandingsPanel } from "./ContestStandingsPanel";
import { SponsorStrip } from "./SponsorStrip";

/**
 * `EX-SCB01`: the scoreboard on the projector at the venue, `?kiosk=1`.
 *
 * A fixed overlay over the whole viewport rather than a page of its own: the
 * Judge shell's topbar and footer are rendered by a server layout that cannot
 * read a query string, and a client hook that hid them after mount would flash
 * the navigation on every load. Covering them costs nothing and hides them on
 * the first paint. Real fullscreen needs a click (browsers require a gesture),
 * so the button is here and the operator presses it once.
 *
 * It holds no data of its own. The standings panel inside makes the same request
 * the standings tab makes, against the same server cache, so a kiosk and a
 * contestant's tab can never disagree (`docs/29` §6.1, ADR-0064 decision 7).
 * That request asks for the `public` view, so an organizer who opens the screen
 * during the freeze puts the frozen board on the wall, not the live order. The
 * badge in the bar says which board it is, so nobody in the hall has to wonder.
 *
 * Dark on purpose (the standings redesign, 2026-09-30): a projector washes a
 * pale tint out at distance, and the bright tiles of the dark ground read from
 * the back of the room.
 */
export function ContestKioskView({
  contest,
  locale,
  onExit,
}: {
  contest: PublicContestDetail;
  locale: string;
  /**
   * A `<Link>` back to `?tab=standings` is a soft navigation on the same route
   * as the page that is already mounted, so the parent's mount-only effect
   * never re-reads the query string and the operator is stuck on the big
   * screen until they refresh by hand. The parent flips its own `kiosk` state
   * instead, the same fix as the button that got them in here.
   */
  onExit: () => void;
}) {
  const t = useTranslations("contest.standings");
  const tPage = useTranslations("contest.page");
  const now = useNow();
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const title = locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en;
  const target = contest.isOpen ? contest.endAt : contest.startAt;
  const remaining = now === null ? null : remainingUntil(target, now);
  const clock = remaining && remaining.total > 0 ? formatRemaining(remaining) : null;

  // The board every contestant is looking at right now: frozen from the freeze
  // until the organizer unfreezes it, which is after the end.
  const publicFrozen = !!contest.freezeAt && (contest.phase === "frozen" || contest.phase === "ended");
  // Not open is either not started or over: the projector goes up before the
  // start, and a bare "Ended" over a countdown read wrong at Weekly #1.
  const notStarted = contest.phase === "draft" || contest.phase === "scheduled";
  const freezeMinute =
    publicFrozen && contest.freezeAt && contest.startAt
      ? formatContestMinute((Date.parse(contest.freezeAt) - Date.parse(contest.startAt)) / 60_000)
      : null;
  // `EX-SPN01`: the sponsors along the screen's foot, read from the back of the hall; no links on a projector.
  const sponsors = contest.brand?.show.bigScreen ? contest.brand.sponsors : [];
  const ghost =
    "inline-flex h-12 items-center gap-2 rounded-[10px] border border-neutral-700 bg-transparent px-4 text-sm font-semibold text-neutral-200 hover:bg-neutral-800 hover:text-white";

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-neutral-950 text-white"
      data-testid="contest-kiosk"
    >
      <header className="flex items-center gap-8 border-b border-neutral-800 bg-neutral-900 px-12 py-4">
        <span
          aria-hidden
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[14px] bg-primary text-[28px] font-bold text-white"
        >
          P
        </span>
        <h1 className="min-w-0 flex-1 truncate font-heading text-4xl font-bold leading-tight tracking-tight">
          {title}
        </h1>
        {publicFrozen ? (
          <span className="inline-flex h-11 shrink-0 items-center gap-2.5 rounded-full bg-blue-900 px-[18px] text-[17px] font-bold tracking-wide text-blue-100">
            <Snowflake className="h-[18px] w-[18px]" aria-hidden />
            {freezeMinute ? t("publicViewFrozen", { at: freezeMinute }) : t("publicView")}
          </span>
        ) : (
          <span
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-2.5 rounded-full px-[18px] text-[17px] font-bold tracking-wide",
              contest.isOpen ? "bg-red-900 text-red-100" : "bg-neutral-800 text-neutral-200",
            )}
          >
            {contest.isOpen ? <i aria-hidden className="block h-2.5 w-2.5 rounded-full bg-current" /> : null}
            {contest.isOpen ? t("kioskLive") : notStarted ? t("kioskUpcoming") : t("kioskEnded")}
          </span>
        )}
        {clock ? (
          <span className="shrink-0 text-right">
            <b className="block font-mono text-[44px] font-bold leading-none tabular-nums">{clock}</b>
            <span className="mt-1.5 block text-base font-semibold uppercase tracking-[0.1em] text-neutral-300">
              {contest.isOpen ? t("kioskLeft") : tPage("startsIn")}
            </span>
          </span>
        ) : null}
        {fullscreen ? null : (
          <Button
            variant="tertiary"
            type="button"
            onClick={() => void document.documentElement.requestFullscreen?.()}
            className={ghost}
          >
            <Maximize2 className="h-4 w-4" aria-hidden />
            {t("fullscreen")}
          </Button>
        )}
        <Button
          variant="tertiary"
          type="button"
          onClick={onExit}
          aria-label={t("exitKiosk")}
          className={cn(ghost, "px-3")}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </header>
      <div className="min-h-0 flex-1 px-12 pb-4 pt-5">
        <ContestStandingsPanel
          slug={contest.slug}
          locale={locale}
          canModerate={contest.viewer.canModerate}
          kiosk
        />
      </div>
      {sponsors.length ? (
        <footer data-testid="kiosk-sponsors" className="flex items-center gap-8 border-t border-neutral-800 bg-neutral-900 px-12 py-4">
          <SponsorStrip sponsors={sponsors} tone="dark" size="screen" links={false} className="min-w-0 flex-1" />
          <span className="shrink-0 text-right">
            <span className="block text-[13px] font-semibold uppercase tracking-[0.14em] text-neutral-400">{t("kioskJudgedOn")}</span>
            <span className="text-[22px] font-bold">
              progsity<span className="text-primary">.</span>io
            </span>
          </span>
        </footer>
      ) : null}
    </div>
  );
}
